// useTyping — the typing indicator for the open thread. Polls who's typing (~2s, the
// server keeps a 6s freshness window) and exposes notifyTyping() for the composer to
// call while the user types (throttled to one heartbeat per 2s).
import { useCallback, useEffect, useRef, useState } from 'react';
import { conversationsApi } from '../api/conversationsApi';

export function useTyping(conversationId: string | null, nameOf: (id?: string) => string) {
  const [typers, setTypers] = useState<string[]>([]);
  const lastSent = useRef(0);

  useEffect(() => {
    if (!conversationId) {
      setTypers([]);
      return;
    }
    let stopped = false;
    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const ids = await conversationsApi.getTyping(conversationId);
        if (!stopped) setTypers(ids);
      } catch {
        /* transient — next tick retries */
      }
    };
    void tick();
    const h = window.setInterval(tick, 2000);
    return () => {
      stopped = true;
      window.clearInterval(h);
    };
  }, [conversationId]);

  const notifyTyping = useCallback(() => {
    if (!conversationId) return;
    const now = Date.now();
    if (now - lastSent.current < 2000) return; // throttle heartbeats
    lastSent.current = now;
    conversationsApi.setTyping(conversationId).catch(() => undefined);
  }, [conversationId]);

  const label =
    typers.length === 0
      ? ''
      : typers.length === 1
        ? `${nameOf(typers[0]) || 'Someone'} is typing…`
        : `${typers.length} people are typing…`;

  return { typers, notifyTyping, label };
}
